import { ChevronDown, Grid3X3, Home, Menu } from "lucide-react";
import {
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui";
import { cn } from "../../lib/utils";
import { getLocalizedAppName } from "../../lib/app-i18n";
import { preloadRemoteDefinition } from "../../lib/remotes";
import { useShellSidebar } from "../../hooks/useShellSidebar";
import { useAuthStore, type TenantApp } from "../../state/auth-store";
import { ShellNotificationsMenu } from "./ShellNotificationsMenu";
import { ShellToastViewport } from "./ShellToastViewport";
import { ShellUserDropdown } from "./ShellUserDropdown";

interface ShellAppNavigationProps {
  apps: TenantApp[];
  currentPath: string;
}

const isAppRouteActive = (currentPath: string, appUrl: string) =>
  currentPath === appUrl || currentPath.startsWith(`${appUrl}/`);

function ShellAppNavigation({ apps, currentPath }: ShellAppNavigationProps) {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const activeApp = apps.find((app) => isAppRouteActive(currentPath, app.url));
  const menuLabel = activeApp
    ? getLocalizedAppName(t, activeApp)
    : t("topbar.navigation.applications");

  return (
    <div className="shell-topbar-navigation min-w-0 flex-1 justify-center">
      <nav
        aria-label={t("topbar.navigation.applications")}
        className="shell-topbar-nav min-w-0 items-center gap-6 text-sm font-medium"
      >
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            cn("shell-nav-link", isActive && "shell-nav-link-active")
          }
        >
          {t("topbar.navigation.home")}
        </NavLink>
        {apps.map((app) => (
          <NavLink
            key={app.key}
            to={app.url}
            onFocus={() => {
              void preloadRemoteDefinition(app.key).catch(() => undefined);
            }}
            onPointerEnter={() => {
              void preloadRemoteDefinition(app.key).catch(() => undefined);
            }}
            className={({ isActive }) =>
              cn("shell-nav-link", isActive && "shell-nav-link-active")
            }
          >
            {getLocalizedAppName(t, app)}
          </NavLink>
        ))}
      </nav>

      <div className="shell-topbar-app-menu">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <button
              type="button"
              aria-label={t("topbar.navigation.openAppSelector")}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-action border border-chrome-border bg-chrome-secondary px-3 text-sm font-medium text-chrome-muted transition-colors hover:text-chrome-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <Grid3X3 className="h-4 w-4" aria-hidden />
              <span className="shell-app-menu-label max-w-36 truncate">
                {menuLabel}
              </span>
              <ChevronDown
                className="shell-app-menu-chevron h-4 w-4 text-chrome-muted"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="top-full w-56">
            <DropdownMenuItem
              icon={<Home />}
              className={cn(
                currentPath === "/" &&
                  "bg-surface-secondary text-content-primary",
              )}
              onSelect={() => navigate("/")}
            >
              {t("topbar.navigation.home")}
            </DropdownMenuItem>
            {apps.map((app) => (
              <DropdownMenuItem
                key={app.key}
                className={cn(
                  isAppRouteActive(currentPath, app.url) &&
                    "bg-surface-secondary text-content-primary",
                )}
                onSelect={() => navigate(app.url)}
              >
                {getLocalizedAppName(t, app)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function ShellLayout({ children }: PropsWithChildren) {
  const { i18n, t } = useTranslation("layout");
  const { user, tenant, apps, logout } = useAuthStore();
  const location = useLocation();
  const isRemoteApp = location.pathname.startsWith("/apps/");
  const navigationApps = apps.filter((app) => app.status === "active");
  const adminApp = navigationApps.find((app) => app.key === "admin");
  const remoteSidebar = useShellSidebar(isRemoteApp);
  const mainRef = useRef<HTMLElement | null>(null);
  const previousPathRef = useRef(location.pathname);
  const previousLanguageRef = useRef(i18n.resolvedLanguage);
  const lastHeadingTextRef = useRef("");
  const [routeAnnouncement, setRouteAnnouncement] = useState("");

  useEffect(() => {
    const main = mainRef.current;
    if (!main) {
      return;
    }

    const rememberHeading = () => {
      lastHeadingTextRef.current =
        main.querySelector<HTMLElement>("h1")?.textContent?.trim() ?? "";
    };
    const observer = new MutationObserver(rememberHeading);
    rememberHeading();
    observer.observe(main, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const pathChanged = previousPathRef.current !== location.pathname;
    const languageChanged =
      previousLanguageRef.current !== i18n.resolvedLanguage;
    const main = mainRef.current;

    if (!pathChanged && !languageChanged) {
      lastHeadingTextRef.current =
        main?.querySelector<HTMLElement>("h1")?.textContent?.trim() ?? "";
      return;
    }
    previousPathRef.current = location.pathname;
    previousLanguageRef.current = i18n.resolvedLanguage;
    setRouteAnnouncement("");

    if (!main) {
      return;
    }

    const previousHeadingText = lastHeadingTextRef.current;
    let settled = false;
    const focusRouteHeading = () => {
      const heading = main.querySelector<HTMLElement>("h1");
      if (!heading) {
        return false;
      }

      const headingText = heading.textContent?.trim() ?? "";
      if (languageChanged && headingText === previousHeadingText) {
        return false;
      }

      settled = true;
      heading.tabIndex = -1;
      heading.focus();
      lastHeadingTextRef.current = headingText;
      setRouteAnnouncement(headingText || t("accessibility.contentUpdated"));
      return true;
    };

    const frame = window.requestAnimationFrame(focusRouteHeading);
    const observer = new MutationObserver(() => {
      if (focusRouteHeading()) {
        observer.disconnect();
      }
    });
    observer.observe(main, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const timeout = window.setTimeout(() => {
      if (!settled) {
        main.tabIndex = -1;
        main.focus();
        setRouteAnnouncement(t("accessibility.contentUpdated"));
      }
      observer.disconnect();
    }, 3_000);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      observer.disconnect();
    };
  }, [i18n.resolvedLanguage, location.pathname, t]);

  const handleToggleRemoteSidebar = () => {
    remoteSidebar.toggle();
  };

  const remoteSidebarToggleLabel = remoteSidebar.isOpen
    ? t("topbar.sidebar.close")
    : t("topbar.sidebar.open");
  const shellContent = children ?? <Outlet />;

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] flex-col overflow-hidden bg-surface-page">
      <ShellToastViewport />
      <header className="sticky top-0 left-0 right-0 z-50 shrink-0 border-b border-chrome-border bg-chrome-primary text-chrome-content">
        <div className="shell-topbar-chrome flex h-16 w-full max-w-none items-center justify-between px-4">
          <div className="shell-topbar-leading flex min-w-0 shrink-0 items-center gap-3">
            {isRemoteApp && remoteSidebar.isAvailable ? (
              <button
                type="button"
                aria-label={remoteSidebarToggleLabel}
                aria-haspopup="dialog"
                aria-controls={remoteSidebar.controller?.panelId}
                aria-expanded={remoteSidebar.isOpen}
                className="shell-topbar-remote-sidebar-toggle rounded-action border border-chrome-border bg-chrome-secondary p-2 text-chrome-muted transition-colors hover:text-chrome-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                onClick={handleToggleRemoteSidebar}
              >
                <Menu className="h-5 w-5" />
              </button>
            ) : null}
            <Link
              to="/"
              className="shell-brand-link flex min-w-0 items-center gap-3"
            >
              <div className="flex items-center">
                <img
                  src="/assets/Logo_sidebar_light.svg"
                  alt={tenant?.name ?? "Favit ERP"}
                  className="shell-brand-logo shell-brand-logo-light"
                />
                <img
                  src="/assets/Logo_sidebar_dark.svg"
                  alt={tenant?.name ?? "Favit ERP"}
                  className="shell-brand-logo shell-brand-logo-dark"
                />
              </div>
              <div className="shell-brand-copy min-w-0">
                <p className="shell-brand-title truncate">
                  {tenant?.name ?? "Favit ERP"}
                </p>
                <p className="shell-brand-subtitle">
                  {t("topbar.centralizedHome")}
                </p>
              </div>
            </Link>
          </div>
          <ShellAppNavigation
            apps={navigationApps}
            currentPath={location.pathname}
          />
          <div className="relative flex shrink-0 items-center gap-3">
            <ShellNotificationsMenu />
            <ShellUserDropdown
              adminApp={adminApp}
              onLogout={logout}
              user={user}
            />
          </div>
        </div>
      </header>
      <main
        ref={mainRef}
        className={cn(
          "shell-layout-main flex-1 min-h-0 min-w-0 w-full",
          isRemoteApp ? "overflow-hidden bg-surface-page" : "overflow-y-auto",
        )}
      >
        <span
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {routeAnnouncement}
        </span>
        <div
          className={cn(
            isRemoteApp
              ? "flex h-full min-h-0 min-w-0 w-full flex-col"
              : "mx-auto w-full max-w-6xl px-4 py-8",
          )}
        >
          {shellContent}
        </div>
      </main>
    </div>
  );
}
